import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import { apibaseurl } from "../webservice/Webservice_url";
import axios, { AxiosResponse } from "axios";
import { useAuth } from "../context/useAuthContext"; 
import { isTokenExpired } from "../../feature-module/utils/token";
import Swal from "sweetalert2";
 
let isSessionExpiredAlertOpen = false;

interface ApiOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  payload?: Record<string, any> | null;
  autoCall?: boolean;
  dependencies?: any[];
  responseType?: "json" | "blob" | "arraybuffer";
  handleGlobalError?: boolean;
  skipAuthRedirect?: boolean;
}

interface UseApiResult<T> {
  data: T | null;
  loading: boolean;
  error: any;
  callApi: (
    payload?: Record<string, any> | FormData,
    customUrl?: string,
  ) => Promise<T | undefined>;
}

function useApi<T = any>(
  endpoint: string,
  {
    method = "GET",
    payload = null,
    autoCall = true,
    dependencies = [],
    responseType = "json",
    handleGlobalError = true,
    skipAuthRedirect = false,
  }: ApiOptions = {},
): UseApiResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(autoCall);
  const [error, setError] = useState<any>(null);

  const navigate = useNavigate();
  const { auth, logout } = useAuth();
  const token = auth?.token;

  const forceLogout = useCallback(async () => { 
    if (isSessionExpiredAlertOpen) return;

    isSessionExpiredAlertOpen = true;

    try {
      await Swal.fire({
        icon: "warning",
        title: "Session Expired",
        text: "Your session has expired. Please log in again.",
        confirmButtonText: "OK",
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
    } finally {
      // Clear authentication and redirect after the alert closes.
      logout();
      localStorage.removeItem("token");
      localStorage.removeItem("login");
      sessionStorage.removeItem("token");

      navigate("/login", { replace: true });

      isSessionExpiredAlertOpen = false;
    }
  }, [logout, navigate]);

  const callApi = useCallback(
    async (
      customPayload: Record<string, any> | FormData | null = payload,
      customUrl?: string,
    ): Promise<T | undefined> => {
      // Check token expiry before making the API request.
      if (!skipAuthRedirect && token && isTokenExpired(token)) {
        await forceLogout();
        return undefined;
      }

      setLoading(true);

      try {
        const isFormData = customPayload instanceof FormData;

        const config = {
          headers: {
            ...(isFormData
              ? {}
              : { "Content-Type": "application/json" }),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          responseType,
          params:
            method === "GET" &&
            customPayload &&
            Object.keys(customPayload).length > 0
              ? customPayload
              : undefined,
          data:
            method === "DELETE" && customPayload
              ? customPayload
              : undefined,
        };

        const finalUrl = `${apibaseurl}${customUrl ?? endpoint}`;

        let response: AxiosResponse<any>;

        if (method === "POST") {
          response = await axios.post(finalUrl, customPayload, config);
        } else if (method === "PUT") {
          response = await axios.put(finalUrl, customPayload, config);
        } else if (method === "PATCH") {
          response = await axios.patch(finalUrl, customPayload, config);
        } else if (method === "DELETE") {
          response = await axios.delete(finalUrl, config);
        } else {
          response = await axios.get(finalUrl, config);
        }

        setData(response.data);
        setError(null);

        return response.data;
      } catch (err: any) {
        setError(err);

        if (axios.isAxiosError(err)) {
          const status = err.response?.status;
          const errorCode = err.response?.data?.code;
          const errorMessage =
            err.response?.data?.error ||
            err.response?.data?.message ||
            "";

          const isUnauthorized =
            status === 401 ||
            errorCode === "UNAUTHORIZED" ||
            /invalid or expired token/i.test(errorMessage);

          if (isUnauthorized && !skipAuthRedirect) {
            await forceLogout();
            throw err;
          }

          if (
            status === 500 ||
            err.code === "ERR_NETWORK" ||
            !err.response
          ) {
            const message = !err.response
              ? "Network error. Please check your connection."
              : "Something went wrong on our end. Please try again.";

            console.error(message);

            if (handleGlobalError) {
              navigate("/error-500");
            }
          }
        } 
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [
      endpoint,
      method,
      payload,
      navigate,
      handleGlobalError,
      skipAuthRedirect,
      token,
      forceLogout,
      responseType,
    ],
  );

  useEffect(() => {
    if (autoCall) {
      callApi().catch(() => {});
    }
  }, [...dependencies, callApi, autoCall]);

  return { data, loading, error, callApi };
}

export default useApi;  